import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import api from "@/common/utils/api";
import { ErrorResponse } from "@reflag/shared/api";
import { StageDTO, StagesQuery } from "@reflag/shared/stageAPI";

import { stageQueryKeys } from "./stageQueryKeys";

export const useStagesData = <TData = StageDTO[]>(
  searchParams?: StagesQuery,
  queryOptions?: Omit<
    UseQueryOptions<StageDTO[], AxiosError<ErrorResponse>, TData>,
    "queryKey" | "queryFn" | "initialData"
  > & { initialData?: () => undefined },
) => {
  const { appId } = useEnvironment();

  // Avoid literal undefined as it becomes the string 'undefined' in search params
  const cleanSearchParams: StagesQuery | undefined =
    searchParams &&
    Object.fromEntries(
      Object.entries(searchParams).filter(([_key, value]) => {
        return typeof value !== "undefined";
      }),
    );

  const dataUrl = `/apps/${appId}/stages?${new URLSearchParams(
    cleanSearchParams as Record<string, string>,
  ).toString()}`;

  return useQuery({
    queryKey: stageQueryKeys.list(appId, cleanSearchParams),

    queryFn: () =>
      api.get<"/apps/:appId/stages">(dataUrl).then((res) => res.data.stages),
    enabled: !!appId,
    ...queryOptions,
  });
};
